/* eslint-disable no-restricted-syntax */
import {
  Locale,
  StaticHostConfig,
  getHostConfigByHostname_UNSAFE,
} from '@aboutyou/config';
import {DataFetcherProvider, GrpcConfig} from '@aboutyou/data-fetcher';
import {
  FeatureFlagSetup,
  FeatureToggleOverrides,
  config as featureFlagConfig,
  mergeFeatureFlagsConfigs,
} from '@aboutyou/feature-flags';
import {
  Country,
  Device,
  Language,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/config.types';
import {
  StaticI18nConfig,
  Translations,
  getI18nConfig_UNSAFE,
} from '@aboutyou/i18n';
import {
  MemoryRouter,
  Route,
  StaticRoutesConfig,
  getRoutesConfig_UNSAFE,
} from '@aboutyou/router';
import {StorageState} from '@aboutyou/storage';
import {BasketTokenProvider} from '@aboutyou/user';
import React from 'react';
import {MarkOptional} from 'ts-essentials';
import {StaticConfig} from '../../bootstrap/src/staticConfig/types';
import {FeaturesProvider} from './FeaturesProvider';
// eslint-disable-next-line monorepo/no-internal-import, monorepo/no-relative-import

type FeaturesProviderProps = React.ComponentProps<typeof FeaturesProvider>;

const locale = Locale.de_DE;

const defaultGermanyHostConfig =
  getHostConfigByHostname_UNSAFE('m.aboutyou.de');

const defaultHostConfig: StaticHostConfig = {
  device: 'mobile' as const,
  tadaridaUrl: 'http://localhost:8456',
  tadaridaRestUrl: 'http://localhost:8455',
  gripsUrl: 'http://localhost:8457',
  environment: 'production',
  locale,
  hostname: 'www.aboutyou.de',
  publicProtocol: 'http',
  publicPort: undefined,
  klarnaSdkUrl: 'https://x.klarnacdn.net/postpurchaseexperience/lib/v1/sdk.js',
  ayOutletUrl: 'https://aboutyou-outlet.de',
  assetsUrl: 'https://www.aboutyou.de',
  legacyCheckout: 'https://checkout.aboutyou.de',
  nativeLanguage: 'Deutsch',
  alternateLocales: defaultGermanyHostConfig?.alternateLocales,
  cloudflareChallengeSiteKey: '',
  cloudflareChallengeDefaultSiteKey: '',
  legacyConfig: {
    shopId: 139,
    childShopId: 428,
    help_center_GET: 'http://localhost:8459/help-center/de',
    sms_POST: 'http://localhost:8459/sms/de',
  },
  checkoutFrontendHost: 'https://checkout-v3.aboutyou-staging.de',
  cmsUrl: 'https://aboutyou.content.aboutyou.cloud',
  checkoutShopsApiUrl: `https://checkout-cdn.aboutyou.cloud/shops/ayou-prev`,
  checkoutAppUrl: `https://checkout-cdn.aboutyou.cloud/app/staging`,
  checkoutTrackingUrl: 'https://cot.aboutyou-staging.com',
  customerEmail: 'kundenservice@aboutyou.de',
  zalandoUrl: 'https://www.zalando.de/',
};

const defaultGrpcConfig = {
  country: Country.GERMANY,
  language: Language.GERMAN,
  device: Device.MOBILE_WEB,
  clientVersion: '',
  traceId: '__tests__',
  abTests: [],
};

const defaultTranslations = {};

const getDefaultStaticConfig = ({
  featureFlagOverrides,
}: {
  featureFlagOverrides?: Partial<FeatureToggleOverrides>;
}): StaticConfig => ({
  hostConfig: defaultHostConfig,
  serverHostConfig: defaultHostConfig,
  grpcConfig: defaultGrpcConfig,
  i18nConfig: {
    locale,
    translations: defaultTranslations as Translations,
    ...getI18nConfig_UNSAFE(locale),
  },
  routesConfig: getRoutesConfig_UNSAFE(locale, []),
  // storage: {cookies: {}},
  featureFlagConfig: {
    mergedConfig: mergeFeatureFlagsConfigs({
      defaultConfig: featureFlagConfig,
      overrides: featureFlagOverrides,
    }),
  },
  enableRealUserMode: false,
});

const defaultHeadContext = {};

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export type FeaturesProviderDevProps = DeepPartial<
  Omit<FeaturesProviderProps, 'featureFlagSetup' | 'children'> & {
    featureFlagOverrides?: Partial<FeatureToggleOverrides>;
  }
> & {
  dataFetcherOptions?: React.ComponentProps<typeof DataFetcherProvider>;
  routerOptions?: MarkOptional<
    React.ComponentProps<typeof MemoryRouter>,
    'initialLocation'
  >;
  routePath?: string;
  children?: React.ReactNode;
};

export const FeaturesProviderDev: React.FC<FeaturesProviderDevProps> = ({
  headContext = defaultHeadContext,
  dataFetcherOptions = {
    initialData: new Map(),
    isInitialServerRequest: false,
  },
  routerOptions,
  routePath,
  children,
  hostConfig,
  i18nConfig,
  routerConfig,
  grpcConfig,
  storage,
  featureFlagOverrides,
  enableBotMode = false,
}) => {
  const defaultConfig = getDefaultStaticConfig({
    featureFlagOverrides:
      featureFlagOverrides as Partial<FeatureToggleOverrides>,
  });
  const modifiedHostConfig: FeaturesProviderProps['hostConfig'] = {
    ...defaultConfig.hostConfig,
    ...(hostConfig as Partial<StaticHostConfig>),
    legacyConfig: {
      ...defaultConfig.hostConfig.legacyConfig,
      ...hostConfig?.legacyConfig,
    } as FeaturesProviderProps['hostConfig']['legacyConfig'],
  };
  const modifiedGrpcConfig: FeaturesProviderProps['grpcConfig'] = {
    ...defaultConfig.grpcConfig,
    ...(grpcConfig as Partial<GrpcConfig>),
  };
  const modifiedI18nConfig: StaticI18nConfig = {
    ...defaultConfig.i18nConfig,
    currencyConfig: {
      ...defaultConfig.i18nConfig.currencyConfig,
      useIsoSign: true,
    },
    ...i18nConfig,
    translations: {
      ...defaultConfig.i18nConfig.translations,
      ...i18nConfig?.translations,
    },
  } as StaticI18nConfig;
  const modifiedRoutesConfig: StaticRoutesConfig = {
    ...defaultConfig.routesConfig,
    ...routerConfig?.routesConfig,
  } as StaticRoutesConfig;

  const modifiedStorage: StorageState = {
    ...{
      serverCookies: {...storage?.serverCookies},
      browserCookies: {...storage?.browserCookies},
    },
  } as StorageState;

  const initialLocation = routerOptions?.initialLocation ?? {
    pathname: '/',
    host: 'localhost',
    protocol: 'http',
  };

  const featureFlagSetup: FeatureFlagSetup = {
    mergedConfig: defaultConfig.featureFlagConfig.mergedConfig,
  };

  return (
    <DataFetcherProvider {...dataFetcherOptions}>
      <FeaturesProvider
        documentReferrer=""
        enableBotMode={enableBotMode}
        headContext={headContext}
        hostConfig={modifiedHostConfig}
        grpcConfig={modifiedGrpcConfig}
        i18nConfig={modifiedI18nConfig}
        storage={modifiedStorage}
        routerConfig={{
          routesConfig: modifiedRoutesConfig,
          routerOptions: {initialLocation, ...routerOptions},
          OverrideRouter: MemoryRouter,
        }}
        featureFlagSetup={featureFlagSetup}
      >
        <BasketTokenProvider>
          {routePath ? <Route path={routePath}>{children}</Route> : children}
        </BasketTokenProvider>
      </FeaturesProvider>
    </DataFetcherProvider>
  );
};
